/**
 * A defensive reader for `applications.import_metadata`.
 *
 * The column is typed `Json` — a recursive union, not an object — and its
 * contents are produced by an LLM extraction pipeline. Its shape (spec 8.4, as
 * amended by the async-capture spec and by phase 23) is a promise, not a
 * guarantee: ten keys, `{ schema_version, model, captured_at, content_sha256,
 * inferred_fields: string[], warnings: string[], adapter, artwork_rect, error,
 * discard_reason }`.
 *
 * Today's writer always emits all ten, though several may be null. Older rows
 * carry fewer — the first six only, before async capture; no `discard_reason`
 * before phase 23 — so a missing member is ordinary input here, not a defect.
 *
 * - `error` is the failure reason for an `import_status = 'failed'` row. It is
 *   human prose, written for a curator and **never parsed** — no error codes,
 *   no enum, and nothing in any client branches on its content.
 * - `artwork_rect` is advisory geometry in **screenshot pixels**,
 *   `{ x, y, width, height }`, measured by the extension over the captured
 *   viewport. It exists to open a cropper pre-positioned on the poster; the
 *   server never acts on it and neither does anything that must be correct.
 * - `discard_reason` is why the importer set an `import_status = 'discarded'`
 *   row aside (today only `'elapsed'`), and null on every other row. It is read
 *   here as any string, but the UI renders it only through a closed i18n
 *   mapping — known values to their own text, anything else to a generic line —
 *   and never as raw text.
 *
 * This reader narrows; it never casts and never throws. One non-string member
 * in `warnings` would otherwise throw during `.map` and blank the whole review
 * screen, which is a convenient way to get an unreviewed extraction approved by
 * a curator who gives up and switches to another application.
 *
 * Note there is no `missing_fields` member in the persisted shape. "Missing" is
 * derived from the application's own null columns by the caller, not read here.
 */

/** How many list entries will ever be rendered from one metadata blob. */
export const MAX_METADATA_LIST_ENTRIES = 50

/** Advisory screenshot-pixel geometry. Never load-bearing; see the docblock. */
export type ArtworkRectView = {
  x: number
  y: number
  width: number
  height: number
}

export type ImportMetadataView = {
  warnings: string[]
  inferredFields: string[]
  model: string | null
  capturedAt: string | null
  error: string | null
  artworkRect: ArtworkRectView | null
  discardReason: string | null
}

/**
 * Built fresh on every call rather than spread from a module-level constant:
 * a shallow spread would hand every caller the same two array instances, so
 * one component pushing into `warnings` would corrupt the next render.
 */
const emptyView = (): ImportMetadataView => ({
  warnings: [],
  inferredFields: [],
  model: null,
  capturedAt: null,
  error: null,
  artworkRect: null,
  discardReason: null
})

const readStringList = (value: unknown): string[] => {
  if (!Array.isArray(value)) return []
  const strings: string[] = []
  for (const entry of value) {
    if (typeof entry === 'string') strings.push(entry)
    if (strings.length >= MAX_METADATA_LIST_ENTRIES) break
  }
  return strings
}

const readString = (value: unknown): string | null =>
  typeof value === 'string' ? value : null

/**
 * `Number.isFinite` on top of the `typeof` test, not instead of it: `NaN` and
 * the two infinities are all numbers, and all three would reach a cropper as a
 * rectangle that silently positions nothing. A comparison would not catch `NaN`
 * either — every comparison against it is false. The `typeof` half is what
 * narrows `unknown` to `number` without a cast.
 */
const readFiniteNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

/**
 * The result is built key by key rather than returned by reference, the same
 * discipline `readStringList` follows and for the same reason: this blob comes
 * from a pipeline that starts at an untrusted page, so a fifth member — or a
 * getter, or a prototype — never travels onwards.
 */
const readRect = (value: unknown): ArtworkRectView | null => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  const rect = value as Record<string, unknown>
  const x = readFiniteNumber(rect.x)
  const y = readFiniteNumber(rect.y)
  const width = readFiniteNumber(rect.width)
  const height = readFiniteNumber(rect.height)

  if (x === null || y === null || width === null || height === null) {
    return null
  }

  return { x, y, width, height }
}

export const readImportMetadata = (value: unknown): ImportMetadataView => {
  // `typeof null === 'object'`, and a JSON array is an object too. Both are
  // valid `Json` and neither carries the members we read.
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return emptyView()
  }

  const record = value as Record<string, unknown>

  return {
    warnings: readStringList(record.warnings),
    inferredFields: readStringList(record.inferred_fields),
    model: readString(record.model),
    capturedAt: readString(record.captured_at),
    error: readString(record.error),
    artworkRect: readRect(record.artwork_rect),
    discardReason: readString(record.discard_reason)
  }
}
